// this must all be kept up to date with team.ts in the server project (and subscription.ts for sub model)
// all solutions for sharing the code (git submodules, git subtrees, etc) complicated the commit process and are not worth it without more developers

export class SubscriptionData { // class here is just an interface with default values
  hasSub: boolean = false; // initially false, until we find one
  cbId: string = ''; // ChargeBee ID (original email, possibly truncated)
  ownerEmail: string = ''; // Current email, may be different than cbId if too long or owner changed
  companyName: string = '';
  status: string = '';
  active: boolean = false; // derived from status
  planId: string = '';
  planQuantity: number = 0;
  maxMembers: number = 50; // default 50 for no subscription; derived from planId and planQuantity
  adminEmail: string = '';
  stayBasic: boolean = false;
  noEdit: boolean = false;
  noHTML: boolean = false;
  hideGallery: boolean = false;
  hideTable: boolean = false;
  hideTimeline: boolean = false;
  hideCalendar: boolean = false;
  hideOrgchart: boolean = false;
  hideGroups: boolean = true; // default to true so we dont get groups for no subscription
  hideOrgunits: boolean = false; // now shown even if hideGroups is true
  hideStatus: boolean = false;  
  selfManage: boolean = false; // default to false
  multipleDomains: string = ''; // if blank, use domain to get data; if populated, use comma separated domains to match subs and use my_customer to get data
  selectedUsers: boolean = false; // if true only retrieve user from dir with flag set in teamtools 
  userSearch: string = ''; // see https://developers.google.com/admin-sdk/directory/v1/guides/search-users
  defaultLanguage: string = '';  
  announcement: string = '';  
  support: string = 'unknown';
  partner: string = '';
  sessionMinutes: number = 0;
  metaData: any = {};
  custCreatedAt: number = 0; // Chargebee customer creation date (Unix timestamp in seconds)
}

export class TeamMetaData {
  lastUpdated: number = 0;
  subscriptionData: SubscriptionData = new SubscriptionData();
  permission: string = 'unknown';
  membersTruncated: boolean = false;
  untruncatedNumMembers: number = 0; // not including suspended or hidden; due to subscription
  groupsTruncated: boolean = false;
  untruncatedNumGroups: number = 0; // not including hidden
}

export class TeamPhone {
  type: string = ''; // whatever comes from dir: in ui, make m, w, or h, with an optional number after
  val: string = ''; // actual phone number
  constructor(type: string, value: string) {
    this.type = type;
    this.val = value;
  }
}

export class TeamCustomField { // names cannot be changed! alert user
  name: string = ''; // only alphanumeric hyphen and underscore allowed by google
  val: string = ''; // max 500 chars
  constructor(name: string, value: string) {
    this.name = name;
    this.val = value;
  }
}

export class TeamMember {
  id: string = ''; // unique ID; format depends on whether from google or microsoft
  photoUrl: string = ''; // from dir for non-admin access
  photoCacheUrl: string = ''; // url to cache storage when admin access allows photo download
  photoData: string = ''; // only used to store from dir, deleted when writing photo to storage
  photoFormat: string = '';
  firstName: string = '';
  lastName: string = '';
  statusColor: string = '';
  status: string = '';
  title: string = ''; // jobTitle in microsoft
  email: string = '';
  phones: TeamPhone[] = []; // array of phone numbers with type and val; microsoft has array of work, one mobile
  isExternal: boolean = false;
  isAdmin: boolean = false;
  t2Admin: boolean = false;
  t2Owner: boolean = false;
  department: string = '';
  managerEmail: string = '';
  managerId: string = '';
  managerName: string = '';
  staffIds: string[] = [];
  startDate: string = ''; // hireDate in microsoft
  birthday: string = ''; // in microsoft, custom field in google
  groupIds: string[] = [];   // array of groups this member belongs to (direct and non-direct)
  website: string = ''; // mySite in microsoft
  streetAddress: string = ''; // same in microsoft
  city: string = ''; // same in microsoft, locality in google
  state: string = ''; // same in microsoft, region in google
  country: string = ''; // same in microsoft
  postalCode: string = ''; // same in microsoft
  organization: string = ''; // in google each user can only be a member of one orgUnit

  // new fields in google admin console
  employeeId: string = ''; // externalIds[{value: string, type: 'organization'}]
  employeeType: string = ''; // organizations[{primary: true, description: string}]
  costCenter: string = ''; // organizations[{primary: true, costCenter: string}]
  buildingId: string = ''; // locations[{type: 'desk', buildingId: string}] (admin panel also sets {area: 'desk'})
  floorName: string = ''; // locations[{type: 'desk', floorName: string}] (admin panel also sets {area: 'desk'})
  floorSection: string = ''; // locations[{type: 'desk', floorSection: string}] (admin panel also sets {area: 'desk'})

  // new fields, all in teamtools customSchema for google, native for microsoft
  description: string = '';
  interests: string = '';
  skills: string = '';
  responsibilities: string = '';
  pastProjects: string = '';
  preferredLanguage: string = '';
  preferredName: string = '';
  schools: string = '';

  // custom fields, defined by user
  customFields: TeamCustomField[] = [];

  // "index signature": allows accessing properties dynamically
  [propName: string]: any;

  // extras to consider
  // ownedDevices, registeredDevices // in ms, custom in google
  // guest type in ms (doesnt seem fixed), external contacts in google
  // shared contacts: see https://developers.google.com/admin-sdk/domain-shared-contacts/?csw=1
}

export class TeamGroup {
  id: string = ''; // unique ID; format depends on whether from google or microsoft
  name: string = '';
  email: string = '';
  description: string = '';
  memberIds: string[] = []; // ids of members of group (direct and non-direct)
}

export class Team {
  metaData: TeamMetaData = new TeamMetaData();
  logoUrl: string = ''; // doesn't exist for ms, we will have to generate from name maybe?
  customFields: string[] = [];
  members: TeamMember[] = [];
  hiddenMembers: TeamMember[] = [];
  // do not get truncated members to avoid huge unpaid accounts, no reason to save gSuspended / msDisabled users
  groups: TeamGroup[] = [];
  hiddenGroups: TeamGroup[] = [];
  truncatedGroups: TeamGroup[] = []; // get truncated groups since small and otherwise no way to hide them
  strayMemberIds: string[] = []; // members with no manager or staff
  topMemberIds: string[] = []; // members with no manager, but staff
  name: string = ''; // use hosted domain in google, displayName or org in ms
  // membersById: any = {}; // members as object indexed by id; see no need for this except on client, so have it do it
  orgs: string[] = []; // list of names for now
  partnerUrls: any = {logo: '', link: ''};
}